import React, { useEffect, useState } from 'react'
import { useTableHandlers } from 'hooks/useTableHandlers'
import { notifyError, notifyWarning } from 'utils'
import { Table, Toolbar } from 'shared'
import { Audience, AudienceListRequest } from '../types'
import { useGetAudienceListQuery } from '../audienceApiSlice'
import CreateEditAudiences from '../createEditAudience/CreateEditAudiences'
import useAuth from '../../../hooks/Auth'
import '../createEditAudience/CreateEditAudience.scss'

const { Column, HeaderCell, Cell, ActiveStatusCell, DEFAULT_PAGE_SIZE } = Table

const COLUMNS = [
  { key: 'name', label: 'Name', flexGrowValue: 1 },
  { key: 'countryName', label: 'Country', flexGrowValue: 1 },
  { key: 'stateName', label: 'State', flexGrowValue: 1 },
  { key: 'districtName', label: 'District', flexGrowValue: 1 },
  { key: 'talukaName', label: 'Sub-District', flexGrowValue: 1 },
  { key: 'cropName', label: 'Crop', flexGrowValue: 1 },
  { key: 'active', label: 'Active', flexGrowValue: 1 }
]

const AudienceList: React.FC = () => {
  const [openAudienceModal, setOpenAudienceModal] = useState<boolean>(false)
  const [isEditMode, setIsEditMode] = useState<boolean>(true)
  const { setopenEditProfile, isSubsId, isCompanyVerified } = useAuth()
  const [selectedAudience, setSelectedAudience] = useState<Audience | null>(
    null
  )
  const [tableData, setTableData] = useState([])
  const { requestBody, onPageChange, onSortColumn, onSearchChange } =
    useTableHandlers<Audience, AudienceListRequest>(
      {
        page: { size: 10, number: 1 }
      },
      'name'
    )

  const { data, error, isLoading, isFetching } =
    useGetAudienceListQuery(requestBody)
  const total = data?.totalElements ?? data?.content?.length ?? 0
  useEffect(() => {
    if (error && !isLoading) {
      notifyError('Failed to load audience  list')
    }
  }, [error, isLoading])

  const handleAudienceAction = (rowData: Audience | null, value: boolean) => {
    if (!isSubsId) {
      setopenEditProfile(true)
    } else if (!isCompanyVerified) {
      notifyWarning(
        'Sorry, your company documents are not verified yet. Once they verified, our team will contact you soon.'
      )
    } else {
      setIsEditMode(value)
      setSelectedAudience(rowData)
      setOpenAudienceModal(true)
    }
  }

  const transformData = (tData: any) => {
    return (
      tData?.content?.map((item: Audience, index: any) => ({
        ...item,
        countryName: item.country?.name || '-',
        stateName: item.state?.name || '-',
        districtName: item.district?.name || '-',
        talukaName: item.taluka?.name || '-',
        cropName: item.crop?.name || '-'
      })) || []
    )
  }

  useEffect(() => {
    if (data?.content) {
      setTableData(transformData(data))
    }
  }, [data])
  return (
    <div className="audience_list">
      <Toolbar
        options={[
          {
            label: 'All',
            value: '',
            onClick: () => {}
          }
        ]}
        onSearchChange={onSearchChange}
        onButtonClick={() => handleAudienceAction(null, true)}
        buttonName="Create Audience"
        total={total}
      />
      <div className="audience-list__main-container">
        <Table
          data={tableData ?? []}
          loading={isFetching}
          paginated
          total={data?.totalElements ?? 0}
          onPageChange={onPageChange}
          onSortColumn={onSortColumn}
          pageSizeOptions={[DEFAULT_PAGE_SIZE, 20, 30]}
          defaultPageSize={DEFAULT_PAGE_SIZE}
          onRowClick={(rowData) => handleAudienceAction(rowData, false)}
        >
          {COLUMNS.map((column, index) => {
            const { key, label, flexGrowValue } = column
            return (
              <Column
                flexGrow={flexGrowValue ?? 1}
                key={key}
                align={index === 0 ? 'left' : 'center'}
                sortable
              >
                <HeaderCell>{label}</HeaderCell>
                {key === 'active' ? (
                  <ActiveStatusCell dataKey={key} />
                ) : (
                  <Cell dataKey={key} />
                )}
              </Column>
            )
          })}
        </Table>
        {openAudienceModal && (
          <CreateEditAudiences
            open={openAudienceModal}
            setOpen={setOpenAudienceModal}
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
            audienceData={selectedAudience}
          />
        )}
      </div>
    </div>
  )
}

export default AudienceList
